import { useMemo, useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './theme';
import Dashboard from '@/pages/Dashboard';
import SimulationView from '@/pages/SimulationView';
import ResultsView from '@/pages/ResultsView';
import NotificationSystem from '@/components/NotificationSystem';
import { useSessionStore } from '@/stores';
import { parseEdgeHubLaunchParams, setEdgeHubToken, decodeJwtClaims } from '@/services/api';

function App() {
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    // Load theme preference from localStorage, default to dark
    const saved = localStorage.getItem('theme-mode');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  // Initialize EdgeHub context from URL parameters
  useEffect(() => {
    const launch = parseEdgeHubLaunchParams();
    if (launch) {
      setEdgeHubToken(launch.token);
      const claims = decodeJwtClaims(launch.token);
      if (claims) {
        useSessionStore.getState().setEdgeHubContext(
          launch.token,
          claims.userId,
          claims.name
        );
      }
      if (launch.scenario) {
        useSessionStore.getState().setScenarioFromUrl(launch.scenario);
      }
    }
  }, []);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme-mode', themeMode);
    // Update data-theme attribute for CSS custom properties
    document.documentElement.setAttribute('data-theme', themeMode);
    if (themeMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeMode]);

  // Provide theme toggle function via window object for global access
  // This allows components to toggle theme without prop drilling
  useEffect(() => {
    (window as any).toggleTheme = () => {
      setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
    };
    (window as any).currentTheme = themeMode;
  }, [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/simulation/:sessionId" element={<SimulationView />} />
          <Route path="/results/:sessionId" element={<ResultsView />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
        <NotificationSystem />
      </Box>
    </ThemeProvider>
  );
}

export default App;